import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Mic,
  Trash2,
  Upload,
  Voicemail as VoicemailIcon,
  X,
  Loader2,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  useDeleteVmDrop,
  useUploadVmDrop,
  useVmDrops,
} from '@/api/powerDialer'

interface Props {
  open: boolean
  onClose: () => void
  darkMode: boolean
}

async function getAudioDuration(blob: Blob): Promise<number> {
  return new Promise((resolve) => {
    const audio = new Audio()
    audio.preload = 'metadata'
    audio.src = URL.createObjectURL(blob)
    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(audio.src)
      resolve(Number.isFinite(audio.duration) ? audio.duration : 0)
    }
    audio.onerror = () => {
      URL.revokeObjectURL(audio.src)
      resolve(0)
    }
  })
}

export function PdVmManagerModal({ open, onClose, darkMode }: Props) {
  const { t } = useTranslation()
  const [vmNewName, setVmNewName] = useState('')
  const [recording, setRecording] = useState(false)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const nameRef = useRef('')

  const { data: vmDrops = [] } = useVmDrops()
  const uploadDrop = useUploadVmDrop()
  const deleteDrop = useDeleteVmDrop()

  // Sync nameRef avec state pour closure dans onstop
  nameRef.current = vmNewName

  const border = darkMode ? 'border-slate-700' : 'border-slate-200'
  const inputBg = darkMode ? 'bg-slate-900' : 'bg-white'
  const textPrimary = darkMode ? 'text-white' : 'text-slate-900'
  const textSecondary = darkMode ? 'text-slate-300' : 'text-slate-600'
  const textMuted = darkMode ? 'text-slate-500' : 'text-slate-400'

  const startRecording = async () => {
    if (!vmNewName.trim()) return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      chunksRef.current = []
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' })
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop())
        streamRef.current = null
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        const duration = await getAudioDuration(blob)
        const name = nameRef.current.trim() || t('powerDialer.vm.untitled', 'Sans titre')
        try {
          await uploadDrop.mutateAsync({ audio: blob, name, duration })
          setVmNewName('')
          toast.success(t('toasts.vmDrop.recorded'))
        } catch {
          toast.error(t('toasts.common.uploadError'))
        }
      }
      recorder.start()
      setRecording(true)
    } catch (err) {
      console.error('[VM] mic error:', err)
      toast.error(t('toasts.vmDrop.micError'))
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    setRecording(false)
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !vmNewName.trim()) return
    try {
      const duration = await getAudioDuration(file)
      await uploadDrop.mutateAsync({ audio: file, name: vmNewName.trim(), duration })
      setVmNewName('')
      toast.success(t('toasts.vmDrop.uploaded'))
    } catch {
      toast.error(t('toasts.common.uploadError'))
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteDrop.mutateAsync(id)
      toast.success(t('toasts.vmDrop.deleted'))
    } catch {
      toast.error(t('toasts.common.deleteError'))
    }
  }

  const handleClose = () => {
    if (recording) stopRecording()
    setVmNewName('')
    onClose()
  }

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] p-4"
      onClick={handleClose}
    >
      <div
        className={cn(
          'rounded-2xl shadow-2xl w-full max-w-md',
          darkMode ? 'bg-slate-800' : 'bg-white',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={cn(
            'flex items-center justify-between px-5 py-4 border-b',
            border,
          )}
        >
          <h3 className={cn('font-semibold', textPrimary)}>
            {t('powerDialer.vm.title', 'Messages vocaux (VM Drop)')}
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className={cn(textMuted, darkMode ? 'hover:text-white' : 'hover:text-slate-900')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Enregistrer un nouveau VM */}
          <div
            className={cn(
              'rounded-xl border p-4',
              darkMode
                ? 'border-slate-700 bg-slate-800/50'
                : 'border-slate-200 bg-slate-50',
            )}
          >
            <label
              className={cn('text-xs font-medium block mb-2', textSecondary)}
            >
              {t('powerDialer.vm.newLabel', 'Nouveau message vocal')}
            </label>
            <input
              type="text"
              value={vmNewName}
              onChange={(e) => setVmNewName(e.target.value)}
              placeholder={t('powerDialer.vm.namePlaceholder', 'Nom du message (ex: Intro standard)')}
              disabled={recording}
              className={cn(
                'w-full text-sm rounded-lg border px-3 py-2 mb-3 outline-none focus:ring-1 focus:ring-[#E85D3A]',
                inputBg,
                border,
                textPrimary,
              )}
            />
            <div className="flex gap-2">
              {!recording ? (
                <button
                  type="button"
                  onClick={() => void startRecording()}
                  disabled={!vmNewName.trim() || uploadDrop.isPending}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-colors disabled:opacity-40',
                    darkMode
                      ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                      : 'bg-purple-50 text-purple-600 hover:bg-purple-100',
                  )}
                >
                  {uploadDrop.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Mic className="w-3.5 h-3.5" />
                  )}
                  {uploadDrop.isPending
                    ? t('powerDialer.vm.sending', 'Envoi...')
                    : t('powerDialer.vm.record', 'Enregistrer')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={stopRecording}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-bold bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  {t('powerDialer.vm.stop', "Arrêter l'enregistrement")}
                </button>
              )}
              <label
                className={cn(
                  'flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-medium cursor-pointer transition-colors',
                  (!vmNewName.trim() || uploadDrop.isPending || recording) &&
                    'opacity-40 pointer-events-none',
                  darkMode
                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    : 'bg-slate-200 text-slate-600 hover:bg-slate-300',
                )}
              >
                <Upload className="w-3.5 h-3.5" />
                {t('powerDialer.vm.file', 'Fichier')}
                <input
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => void handleFileUpload(e)}
                />
              </label>
            </div>
          </div>

          {/* Liste des VM existants */}
          {vmDrops.length === 0 ? (
            <p className={cn('text-center text-sm py-4', textMuted)}>
              {t('powerDialer.vm.empty', 'Aucun message vocal enregistré')}
            </p>
          ) : (
            <div className="space-y-2">
              {vmDrops.map((vm) => (
                <div
                  key={vm.id}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 border',
                    darkMode
                      ? 'border-slate-700 bg-slate-800/30'
                      : 'border-slate-200 bg-white',
                  )}
                >
                  <VoicemailIcon
                    className={cn(
                      'w-5 h-5 flex-shrink-0',
                      darkMode ? 'text-purple-400' : 'text-purple-600',
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div
                      className={cn('text-sm font-medium truncate', textPrimary)}
                    >
                      {vm.name}
                    </div>
                    <div className={cn('text-xs', textMuted)}>
                      {Math.round(vm.duration)}s
                    </div>
                  </div>
                  {vm.audio_url && (
                    <audio controls src={vm.audio_url} className="h-7 w-32" />
                  )}
                  <button
                    type="button"
                    onClick={() => void handleDelete(vm.id)}
                    disabled={deleteDrop.isPending}
                    className={cn(
                      'p-1 rounded-lg transition-colors disabled:opacity-50',
                      darkMode
                        ? 'hover:bg-red-500/20 text-slate-500 hover:text-red-400'
                        : 'hover:bg-red-50 text-slate-400 hover:text-red-500',
                    )}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
